import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, InputNumber, message, Popconfirm, Tabs, Card, TimePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, TableOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import moment from 'moment';
import * as XLSX from 'xlsx';

const { Option } = Select;
const { TabPane } = Tabs;

const CourseScheduleManagement = ({ classId, classInfo }) => {
  const [schedules, setSchedules] = useState([]); // 作息时间表
  const [courses, setCourses] = useState([]); // 课程表
  const [loading, setLoading] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [courseModalVisible, setCourseModalVisible] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('schedule');
  const scheduleFileInputRef = useRef(null);
  const courseFileInputRef = useRef(null);
  
  // 新增：模板相关状态
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // 新增：课程表编辑状态
  const [editingCell, setEditingCell] = useState(null); // { dayKey, periodName }
  const [editingValue, setEditingValue] = useState('');

  // 课程表数据结构：按星期和时段组织
  const [courseTable, setCourseTable] = useState({});

  const weekDays = [
    { key: 1, label: '星期一' },
    { key: 2, label: '星期二' },
    { key: 3, label: '星期三' },
    { key: 4, label: '星期四' },
    { key: 5, label: '星期五' },
    { key: 6, label: '星期六' },
    { key: 7, label: '星期日' }
  ];

  useEffect(() => {
    if (classId) {
      fetchSchedules();
      fetchCourses();
      fetchTemplates();
    }
  }, [classId]);

  const normalizeScheduleTimeValue = (value = '') => {
    const raw = String(value || '').trim();
    if (!raw) return '';

    if (/^\d{1,2}:\d{1,2}(:\d{1,2})?$/.test(raw)) {
      const parts = raw.split(':').map((part) => part.padStart(2, '0'));
      while (parts.length < 3) parts.push('00');
      return parts.join(':').slice(0, 8);
    }

    const digits = raw.replace(/\D/g, '').slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4)}`;
  };

  const isValidScheduleTime = (value) => moment(value, 'HH:mm:ss', true).isValid();

  /** 将表单里的时间（dayjs 或字符串）转为 HH:mm:ss */
  const formTimeToHms = (value) => {
    if (value == null || value === '') return '';
    if (dayjs.isDayjs(value)) {
      if (!value.isValid()) return '';
      return value.format('HH:mm:ss');
    }
    return normalizeScheduleTimeValue(String(value));
  };

  /** 后端/字符串时间 -> TimePicker 用的 dayjs（仅时分秒，日期用当天占位） */
  const hmsToDayjs = (raw) => {
    const normalized = normalizeScheduleTimeValue(String(raw || '').trim().substring(0, 8));
    if (!normalized || !isValidScheduleTime(normalized)) return null;
    const [h, m, s] = normalized.split(':').map((n) => parseInt(n, 10));
    return dayjs().hour(h).minute(m).second(s).millisecond(0);
  };

  // 获取作息时间表
  const fetchSchedules = async ({ fresh = false } = {}) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v2/schedules/school-schedules', {
        // A successful write invalidates the API cache, but the reverse-proxy
        // cache is keyed by URL. Use a one-off cache-busting query only after
        // this page itself changes schedule data.
        params: {
          classId,
          schoolId: classInfo?.school_id,
          ...(fresh ? { refreshAt: Date.now() } : {})
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSchedules(response.data.data);
      }
    } catch (error) {
      console.error('获取作息时间表失败:', error);
      message.error('获取作息时间表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取作息时间表模板列表
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v2/schedules/schedule-templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('获取模板列表失败:', error);
    }
  };

  // 应用模板
  const handleApplyTemplate = async () => {
    if (!selectedTemplate) {
      message.warning('请选择一个模板');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/v2/schedules/schedule-templates/apply', {
        templateKey: selectedTemplate,
        schoolId: classInfo?.school_id,
        classId: classId,
        clearExisting: true  // 固定为清除现有数据
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        message.success(response.data.message);
        await fetchSchedules({ fresh: true });
      }
    } catch (error) {
      console.error('应用模板失败:', error);
      message.error('应用模板失败：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 获取课程表
  const fetchCourses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v2/schedules/course-schedules', {
        params: { classId },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const coursesData = response.data.data;
        setCourses(coursesData);
        
        // 组织课程表数据
        const tableData = {};
        coursesData.forEach(course => {
          const key = `${course.day_of_week}-${course.period_name}`;
          tableData[key] = course;
        });
        setCourseTable(tableData);
      }
    } catch (error) {
      console.error('获取课程表失败:', error);
      message.error('获取课程表失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存/更新作息时间段
  const handleSaveSchedule = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const data = {
        schoolId: classInfo?.school_id,
        classId: classId,
        periodName: values.periodName,
        startTime: formTimeToHms(values.startTime),
        endTime: formTimeToHms(values.endTime),
        periodType: values.periodType || 'class',
        sortOrder: values.sortOrder || 0
      };

      if (!isValidScheduleTime(data.startTime) || !isValidScheduleTime(data.endTime)) {
        message.error('请输入有效时间，格式为 HH:mm:ss');
        return;
      }

      if (editingSchedule) {
        // 更新
        await axios.put(`/api/v2/schedules/school-schedules/${editingSchedule.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('作息时间段更新成功');
      } else {
        // 创建
        await axios.post('/api/v2/schedules/school-schedules', data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('作息时间段创建成功');
      }

      setScheduleModalVisible(false);
      scheduleForm.resetFields();
      setEditingSchedule(null);
      await fetchSchedules({ fresh: true });
    } catch (error) {
      console.error('保存作息时间段失败:', error);
      message.error('保存失败');
    }
  };

  // 删除作息时间段
  const handleDeleteSchedule = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/v2/schedules/school-schedules/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('删除成功');
      await fetchSchedules({ fresh: true });
    } catch (error) {
      console.error('删除作息时间段失败:', error);
      message.error('删除失败');
    }
  };

  // 打开添加/编辑作息时间段对话框
  const openScheduleModal = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      scheduleForm.setFieldsValue({
        periodName: schedule.period_name,
        startTime: hmsToDayjs(schedule.start_time),
        endTime: hmsToDayjs(schedule.end_time),
        periodType: schedule.period_type,
        sortOrder: schedule.sort_order
      });
    } else {
      setEditingSchedule(null);
      scheduleForm.resetFields();
    }
    setScheduleModalVisible(true);
  };

  // 更新课程（单个）
  const handleUpdateCourse = (dayOfWeek, periodName, subjectName) => {
    const key = `${dayOfWeek}-${periodName}`;
    const updatedTable = { ...courseTable };
    
    if (subjectName && subjectName.trim()) {
      updatedTable[key] = {
        ...updatedTable[key],
        class_id: classId,
        day_of_week: dayOfWeek,
        period_name: periodName,
        subject_name: subjectName.trim()
      };
    } else {
      delete updatedTable[key];
    }
    
    setCourseTable(updatedTable);
  };
  
  // 双击开始编辑
  const handleCellDoubleClick = (dayKey, periodName) => {
    const key = `${dayKey}-${periodName}`;
    const course = courseTable[key];
    setEditingCell({ dayKey, periodName });
    setEditingValue(course?.subject_name || '');
  };
  
  // 完成编辑
  const handleCellBlur = () => {
    if (editingCell) {
      handleUpdateCourse(editingCell.dayKey, editingCell.periodName, editingValue);
      setEditingCell(null);
      setEditingValue('');
    }
  };
  
  // 编辑时按回车完成
  const handleCellKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    }
  };

  // 批量保存课程表
  const handleSaveCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const coursesArray = Object.entries(courseTable)
        .map(([key, course]) => {
          const separatorIndex = key.indexOf('-');
          const fallbackDay = separatorIndex > -1 ? Number(key.slice(0, separatorIndex)) : null;
          const fallbackPeriod = separatorIndex > -1 ? key.slice(separatorIndex + 1) : '';
          const subjectName = String(course.subject_name || '').trim();

          if (!subjectName) {
            return null;
          }

          return {
            dayOfWeek: course.day_of_week ?? fallbackDay,
            periodName: course.period_name ?? fallbackPeriod,
            subjectName,
            teacherName: course.teacher_name || null,
            classroom: course.classroom || null
          };
        })
        .filter(Boolean);

      await axios.post('/api/v2/schedules/course-schedules/batch', {
        classId,
        courses: coursesArray
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('课程表保存成功');
      fetchCourses();
    } catch (error) {
      console.error('保存课程表失败:', error);
      message.error('保存课程表失败');
    }
  };

  // 导出作息时间表到Excel
  const handleExportSchedule = () => {
    if (schedules.length === 0) {
      message.warning('没有数据可导出');
      return;
    }

    const exportData = schedules.map(s => ({
      '排序': s.sort_order,
      '时段名称': s.period_name,
      '开始时间': s.start_time,
      '结束时间': s.end_time,
      '类型': s.period_type === 'class' ? '上课' : s.period_type === 'break' ? '休息' : s.period_type === 'meal' ? '就餐' : '其他'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "作息时间表");
    XLSX.writeFile(workbook, `作息时间表-${classInfo?.name || classId}.xlsx`);
    message.success('导出成功');
  };

  // 导入作息时间表
  const handleImportSchedule = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false, dateNF: 'HH:mm:ss' });

        console.log('解析的Excel数据（前3行）:', jsonData.slice(0, 3)); // 调试用

        if (jsonData.length === 0) {
          message.error('Excel文件为空');
          return;
        }

        // 辅助函数：将Excel时间转换为 HH:MM:SS 格式
        const parseExcelTime = (value) => {
          if (!value) return null;
          
          // 如果已经是字符串格式的时间（如 "08:00:00" 或 "8:00"）
          if (typeof value === 'string') {
            // 如果是 HH:MM 格式，补充秒数
            if (/^\d{1,2}:\d{2}$/.test(value)) {
              const parts = value.split(':');
              return `${parts[0].padStart(2, '0')}:${parts[1]}:00`;
            }
            // 如果是 HH:MM:SS 格式，直接返回
            if (/^\d{1,2}:\d{2}:\d{2}$/.test(value)) {
              const parts = value.split(':');
              return parts.map(p => p.padStart(2, '0')).join(':');
            }
            return value;
          }
          
          // 如果是数字（Excel时间格式：0-1之间的小数，表示一天的分数）
          if (typeof value === 'number') {
            const totalMinutes = Math.round(value * 24 * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
          }
          
          return null;
        };

        // 转换数据格式（使用驼峰命名以匹配后端API）
        const scheduleData = jsonData.map(row => {
          console.log('处理行数据:', row); // 调试用
          return {
            periodName: row['时段名称'],
            startTime: parseExcelTime(row['开始时间']),
            endTime: parseExcelTime(row['结束时间']),
            periodType: row['类型'] === '上课' ? 'class' : row['类型'] === '休息' ? 'break' : row['类型'] === '就餐' ? 'meal' : 'other',
            sortOrder: row['排序'] || 0,
            isActive: true
          };
        });

        console.log('转换后的数据（前3行）:', scheduleData.slice(0, 3)); // 调试用

        // 验证必填字段
        const invalidData = scheduleData.some(s => !s.periodName || !s.startTime || !s.endTime);
        if (invalidData) {
          message.error('Excel数据格式不正确，请确保包含：时段名称、开始时间、结束时间');
          return;
        }

        // 批量保存
        const token = localStorage.getItem('token');
        await axios.put(`/api/v2/schedules/school-schedules/batch/${classId}`, {
          schedules: scheduleData
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        message.success(`成功导入 ${scheduleData.length} 条作息时间`);
        await fetchSchedules({ fresh: true });
      } catch (error) {
        console.error('导入失败:', error);
        message.error('导入失败：' + (error.response?.data?.message || error.message));
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = ''; // 清空input以允许重复选择同一文件
  };

  // 导出课程表模板
  const handleExportCourseTemplate = () => {
    const classSchedules = schedules.filter(s => s.period_type === 'class');
    if (classSchedules.length === 0) {
      message.warning('请先添加作息时间表中的上课时段');
      return;
    }

    const exportData = [];
    classSchedules.forEach(schedule => {
      weekDays.forEach(day => {
        const key = `${day.key}-${schedule.period_name}`;
        const course = courseTable[key];
        exportData.push({
          '星期': day.label,
          '时段名称': schedule.period_name,
          '开始时间': schedule.start_time?.substring(0, 5),
          '结束时间': schedule.end_time?.substring(0, 5),
          '科目名称': course?.subject_name || ''
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "课程表");
    XLSX.writeFile(workbook, `课程表-${classInfo?.name || classId}.xlsx`);
    message.success('导出成功');
  };

  // 导入课程表
  const handleImportCourse = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          message.error('Excel文件为空');
          return;
        }

        // 转换数据格式
        const newCourseTable = {};
        jsonData.forEach(row => {
          const dayLabel = row['星期'];
          const periodName = row['时段名称'];
          const subjectName = row['科目名称'];

          if (!subjectName || !dayLabel || !periodName) return;

          // 找到对应的星期数字
          const dayObj = weekDays.find(d => d.label === dayLabel);
          if (!dayObj) return;

          const key = `${dayObj.key}-${periodName}`;
          newCourseTable[key] = {
            class_id: classId,
            day_of_week: dayObj.key,
            period_name: periodName,
            subject_name: String(subjectName).trim()
          };
        });

        setCourseTable(newCourseTable);
        message.success('导入成功，请点击"保存课程表"按钮保存');
      } catch (error) {
        console.error('导入失败:', error);
        message.error('导入失败：' + (error.message || '请检查Excel格式'));
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  // 作息时间表列
  const scheduleColumns = [
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80
    },
    {
      title: '时段名称',
      dataIndex: 'period_name',
      key: 'period_name'
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time'
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time'
    },
    {
      title: '类型',
      dataIndex: 'period_type',
      key: 'period_type',
      render: (type) => {
        const typeMap = {
          'class': '上课',
          'break': '休息',
          'meal': '就餐',
          'other': '其他'
        };
        return typeMap[type] || type;
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => openScheduleModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个时段吗?"
            onConfirm={() => handleDeleteSchedule(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '20px 0' }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* 作息时间表 */}
        <TabPane tab="作息时间表" key="schedule">
          {/* 模板选择区域 */}
          <Card 
            title="选择作息时间表模板" 
            size="small" 
            style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}
          >
            <div style={{ marginBottom: 16 }}>
              <p style={{ color: '#666', marginBottom: 12, fontSize: 13 }}>
                选择一个适合的作息时间表模板快速设置，或直接自定义作息时间表：
              </p>
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                {templates.map(template => (
                  <Card
                    key={template.key}
                    size="small"
                    hoverable
                    style={{ 
                      flex: 1,
                      cursor: 'pointer',
                      border: selectedTemplate === template.key ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      backgroundColor: selectedTemplate === template.key ? '#e6f7ff' : 'white'
                    }}
                    onClick={() => setSelectedTemplate(template.key)}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        border: '2px solid #1890ff',
                        margin: '0 auto 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: selectedTemplate === template.key ? '#1890ff' : 'white'
                      }}>
                        {selectedTemplate === template.key && (
                          <div style={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: 'white' 
                          }} />
                        )}
                      </div>
                      <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 15 }}>
                        {template.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#999', lineHeight: 1.5 }}>
                        {template.description}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {selectedTemplate && (
                <div style={{ 
                  padding: 12, 
                  backgroundColor: '#fff7e6', 
                  border: '1px solid #ffd591',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ color: '#ff4d4f', fontSize: 12 }}>
                    ⚠️ 应用模板将清除现有作息时间表
                  </span>
                  <Button 
                    type="primary" 
                    onClick={handleApplyTemplate}
                    loading={loading}
                  >
                    应用选中的模板
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* 操作按钮 */}
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => openScheduleModal()}
              >
                添加时段
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={handleExportSchedule}
              >
                导出Excel
              </Button>
              <Button 
                icon={<UploadOutlined />}
                onClick={() => scheduleFileInputRef.current?.click()}
              >
                导入Excel
              </Button>
              <input 
                type="file" 
                ref={scheduleFileInputRef}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleImportSchedule}
              />
              <span style={{ color: '#999', fontSize: 12 }}>
                提示：可使用模板快速设置，也可手动添加或导入Excel
              </span>
            </Space>
          </div>
          
          <Table
            columns={scheduleColumns}
            dataSource={schedules}
            rowKey="id"
            loading={loading}
            pagination={false}
          />
        </TabPane>

        {/* 课程表 */}
        <TabPane tab="课程表" key="course">
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button 
                type="primary" 
                icon={<SaveOutlined />}
                onClick={handleSaveCourses}
                disabled={loading}
              >
                保存课程表
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={handleExportCourseTemplate}
              >
                导出Excel
              </Button>
              <Button 
                icon={<UploadOutlined />}
                onClick={() => courseFileInputRef.current?.click()}
              >
                导入Excel
              </Button>
              <input 
                type="file" 
                ref={courseFileInputRef}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleImportCourse}
              />
              <span style={{ color: '#ff4d4f', fontSize: 12 }}>
                ⚠️ 双击单元格编辑科目，编辑完成后请点击"保存课程表"按钮
              </span>
            </Space>
          </div>

          <Card>
            {schedules.filter(s => s.period_type === 'class').length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <p>请先在"作息时间表"中添加上课时段</p>
                <p style={{ fontSize: 12, marginTop: 8 }}>例如：第一节、第二节、第三节等</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th style={{ border: '1px solid #d9d9d9', padding: 8, minWidth: 100 }}>时段/星期</th>
                      {weekDays.map(day => (
                        <th key={day.key} style={{ border: '1px solid #d9d9d9', padding: 8, minWidth: 100 }}>
                          {day.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.filter(s => s.period_type === 'class').map(schedule => (
                      <tr key={schedule.id}>
                        <td style={{ border: '1px solid #d9d9d9', padding: 8, fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                          {schedule.period_name}
                          <br />
                          <span style={{ fontSize: 12, color: '#999' }}>
                            {schedule.start_time?.substring(0, 5)}-{schedule.end_time?.substring(0, 5)}
                          </span>
                        </td>
                        {weekDays.map(day => {
                          const key = `${day.key}-${schedule.period_name}`;
                          const course = courseTable[key];
                          const isEditing = editingCell?.dayKey === day.key && editingCell?.periodName === schedule.period_name;
                          
                          return (
                            <td 
                              key={key} 
                              style={{ 
                                border: '1px solid #d9d9d9', 
                                padding: 4,
                                cursor: 'pointer',
                                backgroundColor: isEditing ? '#e6f7ff' : 'white'
                              }}
                              onDoubleClick={() => handleCellDoubleClick(day.key, schedule.period_name)}
                              title="双击编辑"
                            >
                              {isEditing ? (
                                <Input
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyPress={handleCellKeyPress}
                                  autoFocus
                                  style={{ border: 'none', textAlign: 'center' }}
                                />
                              ) : (
                                <div style={{ 
                                  textAlign: 'center', 
                                  minHeight: '30px', 
                                  lineHeight: '30px',
                                  color: course?.subject_name ? '#000' : '#ccc'
                                }}>
                                  {course?.subject_name || '双击编辑'}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {/* 添加/编辑作息时间段对话框 */}
      <Modal
        title={editingSchedule ? '编辑时段' : '添加时段'}
        open={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false);
          setEditingSchedule(null);
          scheduleForm.resetFields();
        }}
        onOk={() => scheduleForm.submit()}
        width={500}
      >
        <Form
          form={scheduleForm}
          layout="vertical"
          onFinish={handleSaveSchedule}
        >
          <Form.Item
            label="时段名称"
            name="periodName"
            rules={[{ required: true, message: '请输入时段名称' }]}
          >
            <Input placeholder="如：第一节、第二节、早读等" />
          </Form.Item>

          <Form.Item
            label="开始时间"
            name="startTime"
            rules={[
              { required: true, message: '请选择或输入开始时间' },
              {
                validator: (_, value) => {
                  if (value == null || value === '') return Promise.resolve();
                  const str = formTimeToHms(value);
                  if (isValidScheduleTime(str)) return Promise.resolve();
                  return Promise.reject(new Error('请输入有效时间，格式为 HH:mm:ss'));
                }
              }
            ]}
          >
            <TimePicker
              style={{ width: '100%' }}
              format="HH:mm:ss"
              needConfirm={false}
              allowClear
              showNow
              placeholder="点击展开面板滚轮/点选，也可直接输入"
              inputReadOnly={false}
            />
          </Form.Item>

          <Form.Item
            label="结束时间"
            name="endTime"
            rules={[
              { required: true, message: '请选择或输入结束时间' },
              {
                validator: (_, value) => {
                  if (value == null || value === '') return Promise.resolve();
                  const str = formTimeToHms(value);
                  if (isValidScheduleTime(str)) return Promise.resolve();
                  return Promise.reject(new Error('请输入有效时间，格式为 HH:mm:ss'));
                }
              }
            ]}
          >
            <TimePicker
              style={{ width: '100%' }}
              format="HH:mm:ss"
              needConfirm={false}
              allowClear
              showNow
              placeholder="点击展开面板滚轮/点选，也可直接输入"
              inputReadOnly={false}
            />
          </Form.Item>

          <Form.Item
            label="类型"
            name="periodType"
            initialValue="class"
          >
            <Select>
              <Option value="class">上课</Option>
              <Option value="break">休息</Option>
              <Option value="meal">就餐</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="排序"
            name="sortOrder"
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CourseScheduleManagement;

